import { Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { HttpStatusCode } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import commandsConstants from 'src/constants/commands';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EventSignal } from '../panel/models/event.model';
import { PanelService } from '../panel/services/panel.service';
import { EventService } from '../panel/services/event.service';
import { PanelsService } from '../shared/services/panels.service';
import { SignalSearchIntervalService } from 'src/app/signal-strength/services/signal-search-interval.service';
import { ConfirmActionDialogComponent } from 'src/app/shared/components/confirm-action-dialog/confirm-action-dialog.component';
import { AuthService } from '../auth/services/auth.service';

@Component({
  selector: 'app-signal-strength',
  templateUrl: './signal-strength.component.html',
  styleUrls: ['./signal-strength.component.scss'],
})
export class SignalStrengthComponent {
  timer: number;
  isLoading: boolean = false;
  valueSpinner: number = 0;
  options = {
    duration: 10_000,
  };
  increment = 1;
  intervalDuration = 1_000;

  // Signal value (0-100)
  @Input() signalStrength: number = 0;
  @Input() mac: string;
  @Input() signalType: string;
  @Input() signal: number;
  @Input() deviceId: string;

  intervalService = new SignalSearchIntervalService();

  constructor(
    private readonly dialog: MatDialog,
    private readonly ps: PanelService,
    private readonly snackBar: MatSnackBar,
    private readonly translateService: TranslateService,
    private readonly eventService: EventService,
    public panelsService: PanelsService,
    private readonly authService: AuthService
  ) {}

  // Method that listens for eventService state changes while on the equipment screen
  ngOnChanges() {
    this.eventService.signalUpdatedEvent.subscribe((signalParams: EventSignal) => {
      this.verifyListener(signalParams);
    });
  }

  generateModuleErrorPhrase = () => {
    return (
      this.translateService.instant('SignalConsult.ModuleError') +
      `${this.signalType}.` +
      this.translateService.instant('SignalConsult.DeviceError') +
      this.mac
    );
  };

  generateSignalErrorPhrase = () => {
    return (
      this.translateService.instant('SignalConsult.SignalError') +
      this.mac +
      this.translateService.instant('SignalConsult.SignalError2')
    );
  };

  generateMeshConsultPhrase = () => {
    return this.translateService.instant('SignalConsult.MeshConsultPanelTable').replace('{mac}', this.mac);
  };

  // Method that verifies the information coming through the event, and compares
  // with the macs and signals of each button to update it and stop the spinner if active
  verifyListener = (signalParams: EventSignal) => {
    const macListened = signalParams.mac;
    const signalListened = signalParams.signalType;
    const signal = signalParams.signal;

    if (macListened == this.mac && signalListened == this.signalType) {
      this.signalStrength = signal;

      if (this.isLoading && !signal) {
        this.snackBar.open(
          this.translateService.instant('SignalConsult.NoSignal') +
          this.translateService.instant('SignalConsult.Signal.' + this.signalType), 
          this.translateService.instant('Shared.Close'), 
          this.options
        );
      }
      this.stopInterval();
    }
  };

  // Method that starts the loading spinner
  onRefreshClick = () => {
    // Time in milliseconds
    // 3 minutes (plus 15 seconds for security)
    this.timer = (3 * 60 * 1_000) + (15 * 1_000);

    this.isLoading = true;

    this.startInterval();
  };

  waitTimeForSignalSearch = () => {
    this.valueSpinner += this.increment;

    if (this.valueSpinner < this.timer / this.intervalDuration) return;

    const signalConsultTimeout = this.translateService.instant('SignalConsult.Timeout');

    const signalConsultDeviceError = this.translateService.instant('SignalConsult.DeviceError');

    const errorConsult = `${signalConsultTimeout} ${this.signalType}.${signalConsultDeviceError}${this.mac}`;

    this.snackBar.open(errorConsult, this.translateService.instant('Shared.Close'), this.options);

    this.stopInterval();
  };

  // Method to start the waiting time for signal search
  startInterval = () => {
    this.intervalService.startInterval(this.waitTimeForSignalSearch, this.intervalDuration);
  };

  // Method that handles user interaction when starting signal update, and sends the necessary
  // data to the service to send the query command to the equipment
  startPanelConnection = async () => {
    this.onRefreshClick();

    this.ps.consultLink(this.deviceId, this.signalType).subscribe({
      next: (res: any) => {
        this.handleSuccess(res);
      },

      error: (error: any) => {
        this.handleError(error);
      },
    });
  };

  handleError = (error: any) => {
    this.stopInterval();

    if (error?.status == HttpStatusCode.Unauthorized) {
      return this.snackBar.open(
        this.translateService.instant('Shared.SessionExpired'),
        this.translateService.instant('Shared.Close'),
        this.options
      );
    }

    if (error?.status == HttpStatusCode.GatewayTimeout) {
      return this.snackBar.open(
        this.generateSignalErrorPhrase(),
        this.translateService.instant('Shared.Close'),
        this.options
      );
    }

    const signalErrorPhrase = this.generateSignalErrorPhrase();

    this.snackBar.open(signalErrorPhrase, this.translateService.instant('Shared.Close'), this.options);
  };

  handleSuccess = (res: any) => {
    if (res.success) return;

    this.stopInterval();

    if (res.moduleFailure && this.signalType == commandsConstants.MESH) return this.handleSignalTypeMesh();

    this.handleSignalType(res.reason);
  };

  handleSignalType = (reason = undefined) => {
    this.stopInterval();

    this.signalStrength = 0;

    const errorConsult = reason ?? this.generateModuleErrorPhrase();

    this.snackBar.open(errorConsult, this.translateService.instant('Shared.Close'), this.options);
  };

  handleSignalTypeMeshHandler = (result: boolean) => {
    if (!result) return;

    this.onRefreshClick();

    this.ps.consultLink(this.deviceId, commandsConstants.FORCED_MESH).subscribe({
      next: (secondResponse: any) => {
        if (secondResponse.success) return;

        this.handleSignalType();
      },
      error: (error) => {
        this.handleError(error);
      },
    });
  };

  handleSignalTypeMesh = () => {
    const meshConsultPhrase = this.generateMeshConsultPhrase();

    this.openConfirmDialog(
      meshConsultPhrase,
      this.translateService.instant('Shared.Accept'),
      this.handleSignalTypeMeshHandler
    );
  };

  // Method to stop the spinner through isLoading, and set the timer time
  stopInterval = () => {
    this.isLoading = false;
    this.valueSpinner = 0;

    this.intervalService.stopInterval();
  };

  openConfirmDialog = (promptText: string, submitText: string, callback: Function) => {
    const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      maxWidth: '450px',
      autoFocus: false,
      data: { promptText, submitText },
    });

    dialogRef.afterClosed().subscribe((result) => callback(result));
  };
}
